/**
 * FirebaseService.js
 * Low-level Firebase operations for Data-Next app
 * Handles Firestore queries, Storage operations, and Authentication
 */

import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

class FirebaseService {
  constructor() {
    this.auth = getAuth();
    this.db = getFirestore();
    this.storage = getStorage();
  }

  // ==================== AUTHENTICATION ====================

  getCurrentUser() {
    return this.auth.currentUser;
  }

  async getIdToken() {
    const user = this.getCurrentUser();
    if (!user) throw new Error("No authenticated user");
    return await user.getIdToken();
  }

  // ==================== FIRESTORE - TEAMS ====================

  async getTeams() {
    const user = this.getCurrentUser();
    if (!user) return [];

    const teamsRef = collection(this.db, "dataNextTeams");
    const snapshot = await getDocs(teamsRef);

    const teams = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      const isOwner = data.owners && data.owners.includes(user.uid);
      const isCreator =
        data.creators && data.creators.includes(user.email.toLowerCase());
      const isReader =
        data.readers && data.readers.includes(user.email.toLowerCase());

      if (isOwner || isCreator || isReader) {
        teams.push({
          id: doc.id,
          ...data,
          permissions: {
            isOwner,
            isCreator,
            isReader,
            canWrite: isOwner || isCreator,
            canManage: isOwner,
          },
        });
      }
    });

    return teams;
  }

  async getTeam(teamId) {
    const teamDoc = await getDoc(doc(this.db, "dataNextTeams", teamId));
    if (!teamDoc.exists()) return null;

    const data = teamDoc.data();
    const user = this.getCurrentUser();

    return {
      id: teamDoc.id,
      ...data,
      permissions: {
        isOwner: data.owners && data.owners.includes(user.uid),
        isCreator:
          data.creators && data.creators.includes(user.email.toLowerCase()),
        isReader:
          data.readers && data.readers.includes(user.email.toLowerCase()),
      },
    };
  }

  async createTeam(teamData) {
    const user = this.getCurrentUser();
    if (!user) throw new Error("No authenticated user");

    const teamId = `team_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 9)}`;
    const teamRef = doc(this.db, "dataNextTeams", teamId);

    await setDoc(teamRef, {
      teamName: teamData.name,
      description: teamData.description || "",
      owners: [user.uid],
      creators: [],
      readers: [],
      createdBy: user.uid,
      createdByEmail: user.email,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      settings: {
        maxContextSize: 10485760, // 10MB
        allowedFileTypes: ["pdf", "docx", "png", "jpg", "jpeg", "webp", "txt"],
        defaultAgentSettings: {
          temperature: 0.7,
          maxTokens: 4096,
        },
      },
    });

    return teamId;
  }

  async updateTeam(teamId, updates) {
    const teamRef = doc(this.db, "dataNextTeams", teamId);
    await updateDoc(teamRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  }

  async deleteTeam(teamId) {
    const teamRef = doc(this.db, "dataNextTeams", teamId);
    await deleteDoc(teamRef);
  }

  // ==================== FIRESTORE - CONTEXTS ====================

  async getContexts(teamId) {
    const contextsRef = collection(
      this.db,
      "dataNextTeams",
      teamId,
      "contexts"
    );
    const q = query(contextsRef, orderBy("uploadedAt", "desc"));
    const snapshot = await getDocs(q);

    const contexts = [];
    snapshot.forEach((doc) => {
      contexts.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return contexts;
  }

  async getContext(teamId, contextId) {
    const contextDoc = await getDoc(
      doc(this.db, "dataNextTeams", teamId, "contexts", contextId)
    );
    if (!contextDoc.exists()) return null;

    return {
      id: contextDoc.id,
      ...contextDoc.data(),
    };
  }

  async createContext(teamId, contextData) {
    const user = this.getCurrentUser();
    if (!user) throw new Error("No authenticated user");

    const contextId =
      contextData.contextId ||
      `ctx_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const contextRef = doc(
      this.db,
      "dataNextTeams",
      teamId,
      "contexts",
      contextId
    );

    await setDoc(contextRef, {
      contextId,
      name: contextData.name,
      type: contextData.type,
      description: contextData.description || "",
      fileName: contextData.fileName,
      fileSize: contextData.fileSize,
      mimeType: contextData.mimeType,
      storagePath: contextData.storagePath,
      downloadURL: contextData.downloadURL,
      extractedText: contextData.extractedText || "",
      metadata: contextData.metadata || {},
      tags: contextData.tags || [],
      uploadedBy: user.uid,
      uploadedByEmail: user.email,
      uploadedAt: serverTimestamp(),
      status: contextData.status || "processing",
      error: contextData.error || null,
    });

    return contextId;
  }

  async updateContext(teamId, contextId, updates) {
    const contextRef = doc(
      this.db,
      "dataNextTeams",
      teamId,
      "contexts",
      contextId
    );
    await updateDoc(contextRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  }

  async deleteContext(teamId, contextId) {
    const contextRef = doc(
      this.db,
      "dataNextTeams",
      teamId,
      "contexts",
      contextId
    );
    await deleteDoc(contextRef);
  }

  // ==================== FIRESTORE - AGENT EXECUTIONS ====================

  async getAgentExecutions(teamId, limitCount = 50) {
    const executionsRef = collection(
      this.db,
      "dataNextTeams",
      teamId,
      "agentExecutions"
    );
    const q = query(
      executionsRef,
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);

    const executions = [];
    snapshot.forEach((doc) => {
      executions.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return executions;
  }

  async getAgentExecution(teamId, executionId) {
    const executionDoc = await getDoc(
      doc(this.db, "dataNextTeams", teamId, "agentExecutions", executionId)
    );
    if (!executionDoc.exists()) return null;

    return {
      id: executionDoc.id,
      ...executionDoc.data(),
    };
  }

  async createAgentExecution(teamId, executionData) {
    const user = this.getCurrentUser();
    if (!user) throw new Error("No authenticated user");

    const executionId =
      executionData.executionId ||
      `exec_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const executionRef = doc(
      this.db,
      "dataNextTeams",
      teamId,
      "agentExecutions",
      executionId
    );

    await setDoc(executionRef, {
      executionId,
      agentType: executionData.agentType,
      contextIds: executionData.contextIds,
      prompt: executionData.prompt || "",
      options: executionData.options || {},
      result: executionData.result || null,
      versions: executionData.versions || [],
      status: executionData.status || "pending",
      error: executionData.error || null,
      createdBy: user.uid,
      createdByEmail: user.email,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      completedAt: executionData.completedAt || null,
    });

    return executionId;
  }

  async updateAgentExecution(teamId, executionId, updates) {
    const executionRef = doc(
      this.db,
      "dataNextTeams",
      teamId,
      "agentExecutions",
      executionId
    );
    await updateDoc(executionRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  }

  async deleteAgentExecution(teamId, executionId) {
    const executionRef = doc(
      this.db,
      "dataNextTeams",
      teamId,
      "agentExecutions",
      executionId
    );
    await deleteDoc(executionRef);
  }

  // ==================== FIREBASE STORAGE ====================

  async uploadFile(file, path, onProgress) {
    const storageRef = ref(this.storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) onProgress(progress);
        },
        (error) => {
          console.error("Upload error:", error);
          reject(error);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve({
            downloadURL,
            path: uploadTask.snapshot.ref.fullPath,
            size: uploadTask.snapshot.totalBytes,
          });
        }
      );
    });
  }

  async deleteFile(path) {
    const storageRef = ref(this.storage, path);
    await deleteObject(storageRef);
  }
}

export default FirebaseService;
